import { ArrowBackIosNew } from '@mui/icons-material';
import { Box, Typography, useTheme } from '@mui/material';
import { useMediaQuery } from '@mui/system';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { LibraryUrlSearchParams, Routes } from '@/constants';
import { Link } from '@/containers';
import { checkmarkEthics, checkmarkRegular, headphones } from '@/images';
import { AnalyticsService, EVENTS } from '@/services';
import {
  selectIsTriennialSummaryExpanded,
  useAppSelector,
  useFetchTriennialSummaryQuery,
  userPreferencesActions,
} from '@/store';

export const TriennialSummary = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { data: triennialSummary } = useFetchTriennialSummaryQuery();
  const isTriennialSummaryExpanded = useAppSelector(
    selectIsTriennialSummaryExpanded,
  );
  const totalEthics = triennialSummary
    ? Math.round(
        triennialSummary.yearlySummary.reduce(
          (acc, year) => acc + year.ethics,
          0,
        ) * 10,
      ) / 10
    : 0;
  const totalCredits = triennialSummary
    ? Math.round(
        triennialSummary.yearlySummary.reduce(
          (acc, year) => acc + year.completed,
          0,
        ) * 10,
      ) / 10
    : 0;

  if (!triennialSummary) return null;

  const yearlyRequirement = triennialSummary.triennialRequirement / 3;

  return (
    <Box
      sx={[
        {
          mt: {
            xs: 3,
            sm: 4,
          },
          px: {
            xs: 1.5,
            md: 4,
          },
          py: {
            xs: 1.5,
            md: 3,
          },
          width: '100%',
          bgcolor: 'sand100',
          borderRadius: 2,
          border: theme => `1px solid ${theme.palette.grey100}`,
          boxShadow: '0 2px 3px 0 hsla(0, 0%, 0%, 0.08)',
          overflow: 'hidden',
          height: {
            xs: 50,
            md: 72,
          },
          ...(isTriennialSummaryExpanded && {
            height: 'calc-size(max-content, size);',
          }),
          transition: 'all 0.3s ease',
        },
      ]}>
      {triennialSummary ? (
        <Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              cursor: 'pointer',
              userSelect: 'none',
            }}
            onClick={() => {
              dispatch(
                userPreferencesActions.setIsTriennialSummaryExpanded(
                  !isTriennialSummaryExpanded,
                ),
              );
              AnalyticsService.logEvent(
                EVENTS.TRACKER_EVENTS.TRIENNIAL_SUMMARY_TOGGLED,
                {
                  state: isTriennialSummaryExpanded ? 'collapsed' : 'expanded',
                },
              );
            }}>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexGrow: 1,
                alignItems: 'center',
              }}>
              <Typography variant="h4">Current 3-Year Rolling Cycle</Typography>
              {!isMobile && (
                <Typography variant="caption1Bold" sx={{ color: 'text.light' }}>
                  {triennialSummary.yearlySummary[0].baseYear} -{' '}
                  {triennialSummary.yearlySummary[2].baseYear}
                </Typography>
              )}
            </Box>
            <ArrowBackIosNew
              sx={{
                width: 18,
                height: 18,
                transform: isTriennialSummaryExpanded
                  ? 'rotate(90deg)'
                  : 'rotate(-90deg)',
                transition: 'all 0.3s ease',
              }}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: isMdDown ? 'column' : 'row',
              mt: 3.5,
              gap: 6.25,
              justifyContent: 'space-between',
              width: '100%',
            }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                maxWidth: isMdDown ? '100%' : 'calc(100% - 435px - 50px)',
                width: '100%',
              }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}>
                <Typography variant="body3SemiBold">Total Hours</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5 }}>
                  {totalCredits >= triennialSummary.triennialRequirement && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                      }}>
                      <Typography
                        variant="body3SemiBold"
                        sx={{ color: 'accent.main' }}>
                        Completed!
                      </Typography>
                      <Box
                        component="img"
                        src={checkmarkRegular}
                        sx={{ ml: 0.5, width: 15, height: 15 }}
                      />
                    </Box>
                  )}
                  <Typography variant="body3">
                    {totalCredits} / {triennialSummary.triennialRequirement}{' '}
                    hours
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  width: '100%',
                  height: 8,
                  borderRadius: 4,
                  mt: 1.25,
                  bgcolor: theme => `${theme.palette.primary.light}33`,
                }}>
                <Box
                  sx={{
                    width: `${Math.min(
                      (totalCredits / triennialSummary.triennialRequirement) *
                        100,
                      100,
                    )}%`,
                    height: '100%',
                    borderRadius: 4,
                    bgcolor:
                      totalCredits >= triennialSummary.triennialRequirement
                        ? 'accent.main'
                        : 'primary.light',
                  }}
                />
              </Box>
              <Box
                sx={{
                  mt: 3.75,
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 2,
                  }}>
                  <Typography variant="body3SemiBold">Ethics</Typography>
                  <Box
                    sx={{
                      bgcolor: theme => `${theme.palette.secondary.medium}80`,
                      borderRadius: '5px',
                    }}>
                    <Typography
                      variant="caption1Bold"
                      sx={{
                        color: 'secondary.main',
                        px: 1,
                        py: 0.5,
                      }}>
                      Mandatory
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 1.5,
                  }}>
                  {totalEthics >=
                    triennialSummary.triennialEthicsRequirement && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                      }}>
                      <Typography
                        variant="body3SemiBold"
                        sx={{ color: 'secondary.main' }}>
                        Completed!
                      </Typography>
                      <Box
                        component="img"
                        src={checkmarkEthics}
                        sx={{ ml: 0.5, width: 15, height: 15 }}
                      />
                    </Box>
                  )}
                  <Typography variant="body3">
                    {totalEthics} /{' '}
                    {triennialSummary.triennialEthicsRequirement} hours
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  width: '100%',
                  height: 8,
                  borderRadius: 4,
                  mt: 1.25,
                  bgcolor: theme => `${theme.palette.secondary.main}33`,
                }}>
                <Box
                  sx={{
                    width: `${Math.min(
                      (totalEthics /
                        triennialSummary.triennialEthicsRequirement) *
                        100,
                      100,
                    )}%`,
                    height: '100%',
                    borderRadius: 4,
                    bgcolor: 'secondary.active',
                  }}
                />
              </Box>
              {totalEthics < triennialSummary.triennialEthicsRequirement && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    width: 'fit-content',
                    mt: 3.75,
                    px: '15px',
                    py: 1,
                    borderRadius: 1,
                    bgcolor: theme => `${theme.palette.secondary.medium}A6`,
                    border: theme =>
                      `0.75px solid ${theme.palette.secondary.active}`,
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                  onClick={() => {
                    navigate({
                      pathname: Routes.LIBRARY,
                      search: `?${LibraryUrlSearchParams.CATEGORY_SLUG}=ethics`,
                    });
                  }}>
                  <Box component={'img'} src={headphones} />
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      flexWrap: 'wrap',
                      ml: '15px',
                      gap: 0.75,
                    }}>
                    <Typography
                      variant="caption1Bold"
                      sx={{ color: 'secondary.main' }}
                      noWrap>
                      Only{' '}
                      {Math.round(
                        (triennialSummary.triennialEthicsRequirement -
                          totalEthics) *
                          10,
                      ) / 10}{' '}
                      hours to go!
                    </Typography>
                    <Typography variant="caption1" noWrap>
                      Browse ethics episodes now to complete
                    </Typography>
                  </Box>
                  <ArrowBackIosNew
                    sx={{
                      width: 14,
                      height: 14,
                      color: 'grey150',
                      ml: '15px',
                      transform: 'rotate(180deg)',
                    }}
                  />
                </Box>
              )}
            </Box>
            <Box
              sx={{
                width: {
                  xs: '100%',
                  lg: 400,
                },
                display: 'flex',
                flexDirection: {
                  xs: 'column',
                  sm: 'row',
                },
                gap: 2.5,
              }}>
              {triennialSummary.yearlySummary.map(year => (
                <Box
                  key={year.baseYear}
                  sx={{
                    width: '100%',
                    borderRadius: 2,
                    border: theme => `1px solid ${theme.palette.grey150}`,
                    display: 'flex',
                    flexDirection: {
                      xs: 'row',
                      sm: 'column',
                    },
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: '15px',
                    py: {
                      xs: 3,
                      lg: 2.5,
                    },
                    gap: 2.5,
                  }}>
                  <Typography variant="body3SemiBold">
                    {year.baseYear}
                  </Typography>
                  <Box
                    sx={{
                      width: '100%',
                      height: 22,
                      position: 'relative',
                    }}>
                    <Box
                      sx={{
                        width: '100%',
                        height: 8,
                        borderRadius: 4,
                        bgcolor: theme => `${theme.palette.primary.light}33`,
                        position: 'absolute',
                        top: '50%',
                        left: 0,
                        transform: 'translateY(-50%)',
                      }}>
                      <Box
                        sx={{
                          width: `${Math.min(
                            (year.completed / yearlyRequirement) * 100,
                            100,
                          )}%`,
                          height: '100%',
                          borderRadius: 4,
                          bgcolor:
                            year.completed < yearlyRequirement
                              ? 'primary.light'
                              : 'accent.main',
                        }}
                      />
                    </Box>
                    {year.completed >= yearlyRequirement && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '100%',
                          transform: 'translate(-100%, -50%)',
                        }}
                        component="img"
                        src={checkmarkRegular}
                      />
                    )}
                  </Box>
                  <Box>
                    <Typography variant="body3" noWrap>
                      {Math.round(year.completed * 10) / 10} /{' '}
                      {yearlyRequirement}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
          <Box sx={{ mt: 3.5, display: 'flex', flexDirection: 'row' }}>
            <Typography variant="caption1">
              Track your CPD hours here. Verify requirements with your CPA
              regulatory body. For more info{' '}
              <Link
                inline
                to={
                  'https://intercom.help/lumiq/en/articles/11488359-cpd-hours-for-canadian-accounting-finance-professionals'
                }
                onClick={() => {
                  AnalyticsService.logEvent(
                    EVENTS.TRACKER_EVENTS.TRIENNIAL_LINK_CLICKED,
                  );
                }}>
                click here.
              </Link>
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box sx={{ height: 30 }} />
      )}
    </Box>
  );
};
